/**
 * Reset Session utility (#1873 follow-up — task #67).
 *
 * Recovers from a stuck client-side state by:
 *  1. Force-syncing every queue that holds rep-entered data
 *  2. Auto-submitting a Send Device Report so we capture the action
 *     log + state that led to needing the reset
 *  3. Clearing local state keys that drive UI (dayData, odometer,
 *     impersonation) — preserves auth, build hash, product cache, theme
 *
 * Designed for admin testing (reset between impersonations) and for
 * support to clear stuck UI states without losing real work.
 */

import { syncPendingEntries, isSyncEngineEnabled } from '../hooks/useSyncEngine';
import { drainExpenseQueue } from '../hooks/useExpenseQueue';
import { getPendingExpenseEntries } from './expensesDB';
import { collectDiagnostics } from './diagnosticsCollector';
import { diagnosticsApi } from '../services/api';
import { logEvent } from './actionLog';

/**
 * Counts of items currently pending in each queue. Cheap to compute —
 * meant for the confirmation modal so the user knows what's at risk.
 */
export interface ResetCounts {
  /** Generic offline action queue (localStorage rdm_offline_queue) */
  offlineActions: number;
  /** Visit sync engine pending entries (IDB) — only if engine enabled */
  visitSync: number;
  /** Expense queue pending entries (IDB) */
  expenses: number;
  /** Whether an impersonation session is currently active */
  impersonating: boolean;
  /** Whether the user appears to have an active day */
  hasActiveDay: boolean;
}

export async function getResetCounts(): Promise<ResetCounts> {
  let offlineActions = 0;
  try {
    const raw = localStorage.getItem('rdm_offline_queue');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        offlineActions = parsed.filter(
          (a: { status?: string }) => a?.status === 'pending' || a?.status === 'failed',
        ).length;
      }
    }
  } catch {
    // ignore — malformed JSON treated as 0
  }

  let visitSync = 0;
  if (isSyncEngineEnabled()) {
    try {
      const { getPendingSyncEntries } = await import('./visitDB');
      const entries = await getPendingSyncEntries();
      visitSync = entries.length;
    } catch {
      // ignore — engine not initialized or IDB error
    }
  }

  let expenses = 0;
  try {
    const entries = await getPendingExpenseEntries();
    expenses = entries.length;
  } catch {
    // ignore
  }

  let impersonating = false;
  try {
    impersonating = !!localStorage.getItem('impersonating');
  } catch {
    // ignore
  }

  let hasActiveDay = false;
  try {
    const raw = localStorage.getItem('dayData');
    if (raw) {
      const parsed = JSON.parse(raw);
      hasActiveDay = parsed?.status === 'active';
    }
  } catch {
    // ignore
  }

  return { offlineActions, visitSync, expenses, impersonating, hasActiveDay };
}

export interface ResetResult {
  diagnosticsReportId: number | null;
  syncedOfflineActions: { attempted: number; succeeded: number; failed: number };
  syncedVisitEntries: { attempted: number; succeeded: number; failed: number };
  syncedExpenses: { attempted: number; succeeded: number; failed: number };
  cleared: string[];
  errors: string[];
}

/**
 * Execute the reset. Force-syncs all queues, submits a diagnostics
 * report capturing the pre-reset state, then clears local UI state.
 *
 * Honors `forceClearOnSyncFailure`:
 *  - false (default): if any sync step fails, returns without clearing
 *  - true: clears anyway — user explicitly opted into losing data
 *
 * The caller should `window.location.reload()` after this resolves so
 * fresh state loads from the server.
 */
export async function resetSession(options: { forceClearOnSyncFailure?: boolean } = {}): Promise<ResetResult> {
  const errors: string[] = [];
  const result: ResetResult = {
    diagnosticsReportId: null,
    syncedOfflineActions: { attempted: 0, succeeded: 0, failed: 0 },
    syncedVisitEntries: { attempted: 0, succeeded: 0, failed: 0 },
    syncedExpenses: { attempted: 0, succeeded: 0, failed: 0 },
    cleared: [],
    errors,
  };

  // 1. Submit a diagnostics report BEFORE syncing/clearing so the
  // action log shows what got the rep here. Best-effort — don't let
  // submission failure block the reset.
  logEvent({ kind: 'visibility', note: 'reset-session: snapshot' });
  try {
    const report = await collectDiagnostics({ context: { type: 'quick' } });
    const resp = await diagnosticsApi.submit(report);
    result.diagnosticsReportId = resp.report_id;
  } catch (err) {
    errors.push(`diagnostics snapshot failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  // 2. Force-sync the visit sync engine (priority for in-progress work)
  if (isSyncEngineEnabled()) {
    try {
      const r = await syncPendingEntries();
      result.syncedVisitEntries = { attempted: r.attempted, succeeded: r.succeeded, failed: r.failed };
    } catch (err) {
      errors.push(`visit sync failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // 3. Drain the expense queue
  try {
    const r = await drainExpenseQueue();
    result.syncedExpenses = {
      attempted: r.attempted ?? 0,
      succeeded: r.succeeded ?? 0,
      failed: r.failed ?? 0,
    };
  } catch (err) {
    errors.push(`expense drain failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  // Legacy localStorage 'rdm_offline_queue' (the retired useOfflineSync
  // hook, removed in #1987). The hook no longer exists, so nothing drains
  // this key automatically anymore — any residual rows from an older app
  // build are dead weight. We clear it below in KEYS_TO_CLEAR so a reset
  // sweeps it for good. Live writes now flow through visit_sync_queue.

  const hadSyncFailure = errors.length > 0 || result.syncedVisitEntries.failed > 0 || result.syncedExpenses.failed > 0;
  if (hadSyncFailure && !options.forceClearOnSyncFailure) {
    return result;
  }

  // 4. Clear UI state keys. Auth and friends stay so the rep doesn't
  // have to log back in.
  const KEYS_TO_CLEAR = [
    'dayData',
    'odometerData',
    'impersonating',
    'rdm_offline_queue', // legacy useOfflineSync queue (#1987); sweep dead rows

    // Note: 'associate' is preserved — clearing it would log the rep
    // out of API context. The post-reload Profile load refetches the
    // canonical associate from /api/auth/impersonate/list/ so a stale
    // value is corrected naturally.
  ];
  for (const key of KEYS_TO_CLEAR) {
    try {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        result.cleared.push(`localStorage.${key}`);
      }
    } catch (err) {
      errors.push(`localStorage.removeItem(${key}) failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  return result;
}

/**
 * KEYS the reset preserves. Documented here so a future engineer
 * tempted to "just clear everything" sees what they'd break.
 */
export const PRESERVED_KEYS = [
  'accessToken',
  'refreshToken',
  'associate', // refetched post-reload; clearing would break API auth context
  'lastBuildHash', // build-change detection
  'theme', // user preference
];
